import type { ReactNode } from 'react';
import { useMemo } from 'react';
import Highlighter from 'react-highlight-words';
import { useTranslation } from 'react-i18next';
import { Box, cssVar, HStack, List, ListItem, Text, VStack } from '@chakra-ui/react';
import type { UseComboboxPropGetters, UseSelectPropGetters } from 'downshift';
import { motion } from 'framer-motion';

import { typography } from '@izimi/theme';

/**
 * @see https://github.com/chakra-ui/chakra-ui/pull/7756/files#diff-1509cc5b9f5d413493863b7f4904f51419b6dd7e00219f68a6199203133b62ad
 */
const itemSizes = {
  lg: 12,
  md: 10,
  sm: 8,
  xs: 6,
} as const;

export type BaseItem = Record<string, unknown>;

export type BaseProps<Item extends BaseItem> = {
  items: Item[];
  selectedItem: Item | null;
  isPending?: boolean;
  isDisabled?: boolean;
  isInvalid?: boolean;
  idProperty?: string;
  labelProperty?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  onSelectedItemChange: (item: Item | null) => void;
  noItemsMessage?: string;
  initialHighlightedIndex?: number;
  /** Background of the keyboard/mouse-highlighted row. Defaults to the original teal
   * `primary100`, kept as-is for existing consumers (izimi-web, izimi-notary); pass a
   * different token for a design that's moved to another accent palette. */
  highlightedItemBg?: string;
  /** Color of each item's `subLabel` line. Defaults to the original `neutral400`. */
  subLabelColor?: string;
  /** Optional leading icon per row, e.g. an entity-type icon. Not part of the original
   * design, opt-in only, renders nothing when omitted. */
  renderItemIcon?: (item: Item) => ReactNode;
  /** Border color of the open dropdown list (not the input above it). Defaults to the
   * original teal `primary400`, kept as-is for existing consumers. Pass this alongside the
   * matching input `_focus.borderColor` override, otherwise the input and the list it opens
   * into visibly clash (blue input border, teal list border). */
  accentBorderColor?: string;
  /** Box-shadow of the open dropdown list. Defaults to the original teal glow. */
  accentBoxShadow?: string;
};

type Props<Item extends BaseItem> = {
  children: ReactNode;
  isOpen: boolean;
  highlightedIndex: number;
  inputValue: string;
} & Pick<
  UseSelectPropGetters<Item> | UseComboboxPropGetters<Item>,
  'getMenuProps' | 'getItemProps'
> &
  BaseProps<Item>;

const AnimatedList = motion(List);

const BaseSelect = <Item extends BaseItem>({
  items,
  idProperty = 'id',
  labelProperty = 'label',
  size = 'lg',
  isOpen,
  getMenuProps,
  getItemProps,
  inputValue,
  children,
  isInvalid,
  noItemsMessage,
  highlightedIndex,
  highlightedItemBg = 'primary100',
  subLabelColor = 'neutral400',
  renderItemIcon,
  accentBorderColor = 'primary400',
  accentBoxShadow = '0px 0px 0px 4px rgba(80, 178, 194, 0.1)',
}: Props<Item>) => {
  const { t } = useTranslation('shared');

  const hasSubLabel = items.some((e) => typeof e.subLabel !== 'undefined');
  const itemHeight = itemSizes[size] * (hasSubLabel ? 6 : 4);

  const animateListVariants = useMemo(() => {
    return {
      expanded: {
        height: items.length === 0 ? itemHeight : itemHeight * Math.min(items.length, 4),
        borderWidth: 1.5,
      },
      collapsed: {
        height: 0,
        borderWidth: 0,
      },
    };
  }, [items, itemHeight]);

  return (
    <Box w='100%' position='relative'>
      {children}
      <AnimatedList
        variants={animateListVariants}
        animate={isOpen ? 'expanded' : 'collapsed'}
        variant='selectList'
        borderColor={
          // eslint-disable-next-line no-nested-ternary
          isOpen ? (isInvalid ? 'secondary400' : accentBorderColor) : 'transparent'
        }
        boxShadow={isOpen ? accentBoxShadow : 'none'}
        {...getMenuProps()}>
        {isOpen &&
          (items.length > 0 ? (
            items.map((item, index) => {
              const itemProps = getItemProps({
                item,
                index,
              });
              return (
                <ListItem
                  height={`${itemHeight - 2}px`}
                  key={String(item[idProperty])}
                  backgroundColor={highlightedIndex === index ? highlightedItemBg : 'transparent'}
                  // The theme's own `_hover` (teal `primary100`) wins over the plain
                  // backgroundColor prop above on real mouse hover - repeat it here with the
                  // same token so keyboard-highlight and mouse-hover always agree.
                  _hover={{ backgroundColor: highlightedItemBg }}
                  {...itemProps}>
                  <HStack alignItems='start'>
                    {renderItemIcon?.(item)}
                    <VStack alignItems='start'>
                      <>
                        <Highlighter
                          autoEscape
                          searchWords={[inputValue || '']}
                          textToHighlight={String(item[labelProperty])}
                          highlightStyle={{
                            backgroundColor: 'transparent',
                            color: cssVar('chakra-colors-neutral500').reference,
                            ...typography.input(),
                            fontWeight: '700',
                          }}
                        />
                        {item.subLabel && (
                          <Box mt='0 !important'>
                            <Text variant='smallSemibold' color={subLabelColor}>
                              {String(item.subLabel)}
                            </Text>
                          </Box>
                        )}
                      </>
                    </VStack>
                  </HStack>
                </ListItem>
              );
            })
          ) : (
            <ListItem _hover={{ bg: 'transparent' }} height={`${itemHeight - 2}px`}>
              {noItemsMessage ?? t('select.noItemsFound')}
            </ListItem>
          ))}
      </AnimatedList>
    </Box>
  );
};

export default BaseSelect;
